import './styles.css'
import React from 'react'
import moment from 'moment'

import { Header, Form, Dropdown, Icon, Message, Button } from 'semantic-ui-react'
import { withTranslation } from 'react-i18next'
import BookingDetailsTextarea from '../BookingDetailsTextarea/BookingDetailsTextarea'
import BookingsGeneralNotificationMessage from '../../shared/BookingsGeneralNotificationMessage/index'
import CollectorsDropdown from '../CollectorsDropDown/CollectorsDropDown'
import DetailedViewSkeletonLoader from '../DetailedViewSkeletonLoader/DetailedViewSkeletonLoader'
import KegCollectionRequestsTable from '../KegCollectionRequestsTable/KegCollectionRequestsTable'
import ReactDatepicker, {
  validateDate,
} from '../../../../../../common-components/rewrite/ReactDatepicker'
import UiContainer from '../../../../../../common-components/UiContainer'

import createAction from '../../../../../../redux/actions/createAction'
import {
  FETCH_SINGLE_KEG_COLLECTION_BOOKING,
  UPDATE_KEG_COLLECTION_BOOKING,
  RESET_EDIT_KEG_COLLECTION_REQUEST_STATUS_STATE,
  SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE,
  RESET_DELETE_KEG_COLLECTION_STATUS_STATE,
  HIDE_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE,
  RESET_CURRENT_USER_STATE,
  RESET_EDITED_BOOKING_STATUS_STATE,
} from '../../../../../../redux/actions/actionTypes'
import {
  formatBookingsTableRows,
  makeDateFormatter,
  formatDate,
  formatKegCollectionTableRows,
} from '../../../utils/tableDataFormatter'
import { formatDateForApiCall } from '../../../../../../utils'
import { useStatusOptions } from '../../../utils/useStatusOptions'
import { sanitizedInput } from '../../../../../../utils/formNormalization'

const initialFormValuesState = {
  bookingNotes: '',
  venueNotes: '',
  collectorDropdownValue: null,
  preferredCollectorId: null,
  assignedBy: null,
  statusDropdownValue: null,
  startEstimatedPickupDateValue: null,
  endEstimatedPickupDateValue: null,
  actualPickupDateValue: null,
  startEstimatedPickupDateAPIValue: null,
  endEstimatedPickupDateAPIValue: null,
  actualPickupDateAPIValue: null,
  setAsPreferredCollector: false,
}

const getPreferredCollectorName = (collectorId, collectorsList) => {
  const found = collectorsList.find(collector => collector.id === collectorId)
  return found ? found.name : null
}

const getBookingStatusBadgeConfig = status => {
  const normalizedStatus = status ? String(status).toUpperCase() : null
  switch (normalizedStatus) {
    case 'PENDING':
      return {
        status: normalizedStatus,
        className: 'bk_dv_statusBadge--pending',
        icon: 'clock',
      }
    case 'ASSIGNED':
      return {
        status: normalizedStatus,
        className: 'bk_dv_statusBadge--assigned',
        icon: 'user',
      }
    case 'SCHEDULED':
      return {
        status: normalizedStatus,
        className: 'bk_dv_statusBadge--scheduled',
        icon: 'calendar check',
      }
    case 'COMPLETED':
      return {
        status: normalizedStatus,
        className: 'bk_dv_statusBadge--completed',
        icon: 'check circle',
      }
    case 'CANCELLED':
      return {
        status: normalizedStatus,
        className: 'bk_dv_statusBadge--cancelled',
        icon: 'ban',
      }
    default:
      return {
        status: normalizedStatus || 'UNKNOWN',
        className: 'bk_dv_statusBadge--unknown',
        icon: 'question circle',
      }
  }
}

const KegCollectionBookingDetails = withTranslation()(
  UiContainer({
    containerId: 'kegCollectionBookingsDetails',
  })(props => {
    const {
      dispatch,
      bookingDetails: {
        bookingData,
        deleteKegCollectionRequestStatusState,
        editKegCollectionRequestStatusState,
      },
      collectorsList,
      match,
      t: translate,
      currentUserOperateAs,
      currentUserEmployee,
    } = props

    React.useEffect(() => {
      // Reset current user if current user data exists in Redux store
      // This is needed to prevent network errors
      if (currentUserOperateAs?.id) {
        dispatch(createAction(RESET_CURRENT_USER_STATE))
      }
    }, [currentUserOperateAs, dispatch])

    const locale = navigator.language || undefined
    const dateFormatter = React.useMemo(() => makeDateFormatter(locale), [locale])
    const statusOptions = useStatusOptions(translate)

    const [formValues, setFormValues] = React.useState(initialFormValuesState)
    const [applyErrorStyling, setApplyErrorStyling] = React.useState({
      startEstimatedPickupDateInputError: { error: false, errorMessage: null },
      endEstimatedPickupDateInputError: { error: false, errorMessage: null },
      actualPickupDateInputError: { error: false, errorMessage: null },
    })

    React.useEffect(
      () => () => {
        // Hide any notification messages on unmount
        dispatch(createAction(HIDE_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE))
      },
      [dispatch]
    )

    // Effect to show notification messages based on booking actions
    React.useEffect(() => {
      if (bookingData?.didEditSucceed) {
        dispatch(
          createAction(SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE, {
            header: translate('Success'),
            content: translate('bookingUpdatedSuccessMessage'),
            isError: false,
          })
        )
        dispatch(createAction(RESET_EDITED_BOOKING_STATUS_STATE))
      }
      if (bookingData?.didEditFail) {
        dispatch(
          createAction(SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE, {
            header: translate('Error'),
            content: translate('bookingUpdateErrorMessage'),
            isError: true,
          })
        )
        dispatch(createAction(RESET_EDITED_BOOKING_STATUS_STATE))
      }
      if (deleteKegCollectionRequestStatusState?.didDeleteSucceed) {
        dispatch(
          createAction(SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE, {
            header: translate('Success'),
            content: translate('kegCollectionDeleteSuccessMessage'),
            isError: false,
          })
        )
        dispatch(createAction(RESET_DELETE_KEG_COLLECTION_STATUS_STATE))
      }
      if (editKegCollectionRequestStatusState?.didEditSucceed) {
        dispatch(
          createAction(SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE, {
            header: translate('Success'),
            content: translate('kegCollectionUpdatedSuccessMessage'),
            isError: false,
          })
        )
        dispatch(createAction(RESET_EDIT_KEG_COLLECTION_REQUEST_STATUS_STATE))
      }
    }, [
      bookingData?.didEditSucceed,
      dispatch,
      translate,
      bookingData?.didEditFail,
      deleteKegCollectionRequestStatusState?.didDeleteSucceed,
      editKegCollectionRequestStatusState?.didEditSucceed,
    ])

    React.useEffect(() => {
      // If bookingDetails is not present, fetch it.
      // This will happen if user navigates directly to this view.
      if (!bookingData.content) {
        dispatch(
          createAction(FETCH_SINGLE_KEG_COLLECTION_BOOKING, {
            bookingId: match?.params?.bookingId,
          })
        )
      }
    }, [bookingData.content, match, dispatch])

    const formattedBookingDetails = React.useMemo(() => {
      if (!bookingData.content) return null
      return bookingData.content._sort_customerName
        ? bookingData.content
        : formatBookingsTableRows([bookingData.content], translate, dateFormatter)[0]
    }, [bookingData.content, translate, dateFormatter])

    React.useEffect(() => {
      // Populate form values when formattedBookingDetails is available
      setFormValues(prev => ({
        ...prev,
        bookingNotes: formattedBookingDetails?.bookingNotes || '',
        venueNotes: formattedBookingDetails?.venueNotes || '',
        collectorDropdownValue: formattedBookingDetails?.collectorCustomerId || null,
        statusDropdownValue: formattedBookingDetails?.bookingStatus || null,
        startEstimatedPickupDateValue: formattedBookingDetails?.bookingRawData
          ?.startEstimatedPickupDate
          ? moment(formattedBookingDetails?.bookingRawData?.startEstimatedPickupDate).format(
              'MM/DD/YYYY'
            )
          : null,
        endEstimatedPickupDateValue: formattedBookingDetails?.bookingRawData?.endEstimatedPickupDate
          ? moment(formattedBookingDetails?.bookingRawData?.endEstimatedPickupDate).format(
              'MM/DD/YYYY'
            )
          : null,
        actualPickupDateValue: formattedBookingDetails?.bookingRawData?.actualPickupDate
          ? moment(formattedBookingDetails?.bookingRawData?.actualPickupDate).format('MM/DD/YYYY')
          : null,
        startEstimatedPickupDateAPIValue:
          formattedBookingDetails?.bookingRawData?.startEstimatedPickupDate,
        endEstimatedPickupDateAPIValue:
          formattedBookingDetails?.bookingRawData?.endEstimatedPickupDate,
        actualPickupDateAPIValue: formattedBookingDetails?.bookingRawData?.actualPickupDate,
        setAsPreferredCollector: false,
      }))
    }, [formattedBookingDetails])

    const initialComparable = React.useMemo(() => {
      // Define initial comparable values for dirty checking
      if (!formattedBookingDetails) return null

      const mostRecent = Array.isArray(formattedBookingDetails.kegCollectionRequestObjects)
        ? formattedBookingDetails.kegCollectionRequestObjects.find(
            c => c.id === formattedBookingDetails.mostRecentCollectionRequestId
          )
        : null

      return {
        bookingNotes: (mostRecent?.pickupInstruction ?? '').trim(),
        venueNotes: (formattedBookingDetails?.venueNotes ?? '').trim(),
        collectorCustomerId: formattedBookingDetails?.collectorCustomerId ?? null,
        bookingStatus: formattedBookingDetails?.bookingStatus
          ? String(formattedBookingDetails?.bookingStatus).toUpperCase()
          : null,
        startEstimatedPickupDateAPIValue:
          formattedBookingDetails?.bookingRawData?.startEstimatedPickupDate ?? null,
        endEstimatedPickupDateAPIValue:
          formattedBookingDetails?.bookingRawData?.endEstimatedPickupDate ?? null,
        actualPickupDateAPIValue: formattedBookingDetails?.bookingRawData?.actualPickupDate ?? null,
        setAsPreferredCollector: false,
      }
    }, [formattedBookingDetails])

    const currentComparable = React.useMemo(() => {
      // Define current comparable values for dirty checking
      const toNull = v => (v === '' || v === undefined ? null : v)
      return {
        bookingNotes: (formValues.bookingNotes ?? '').trim(),
        venueNotes: (formValues.venueNotes ?? '').trim(),
        collectorCustomerId: toNull(formValues.collectorDropdownValue),
        bookingStatus: formValues.statusDropdownValue
          ? String(formValues.statusDropdownValue).toUpperCase()
          : null,
        startEstimatedPickupDateAPIValue: toNull(formValues.startEstimatedPickupDateAPIValue),
        endEstimatedPickupDateAPIValue: toNull(formValues.endEstimatedPickupDateAPIValue),
        actualPickupDateAPIValue: toNull(formValues.actualPickupDateAPIValue),
        setAsPreferredCollector: formValues.setAsPreferredCollector,
      }
    }, [formValues])

    const hasErrors =
      applyErrorStyling.startEstimatedPickupDateInputError.error ||
      applyErrorStyling.endEstimatedPickupDateInputError.error ||
      applyErrorStyling.actualPickupDateInputError.error

    const isDirty = React.useMemo(() => {
      // Determine if form is dirty by comparing initial and current comparable values
      if (!initialComparable) return false
      const keys = Object.keys(initialComparable)
      const some = keys.some(k => initialComparable[k] !== currentComparable[k])
      return some
    }, [initialComparable, currentComparable])

    const isBookingClosed = React.useMemo(
      () => formattedBookingDetails?.kegCollectionRequestObjects?.length === 0,
      [formattedBookingDetails]
    )
    const isSaveDisabled =
      isBookingClosed || !isDirty || hasErrors || bookingData.isFetching || bookingData.isEditing

    const statusBadge = getBookingStatusBadgeConfig(formValues.statusDropdownValue)

    // Define date picker field config map
    const datePickerFieldConfigMap = React.useMemo(
      () => ({
        START_ESTIMATED_PICKUP_DATE: {
          formValueKey: 'startEstimatedPickupDateValue',
          apiValueKey: 'startEstimatedPickupDateAPIValue',
          errorKey: 'startEstimatedPickupDateInputError',
          label: translate('startEstimatedPickupDate'),
          inputName: 'bk_dv_startEstimatedPickupDate',
        },
        END_ESTIMATED_PICKUP_DATE: {
          formValueKey: 'endEstimatedPickupDateValue',
          apiValueKey: 'endEstimatedPickupDateAPIValue',
          errorKey: 'endEstimatedPickupDateInputError',
          label: translate('endEstimatedPickupDate'),
          inputName: 'bk_dv_endEstimatedPickupDate',
        },
        ACTUAL_PICKUP_DATE: {
          formValueKey: 'actualPickupDateValue',
          apiValueKey: 'actualPickupDateAPIValue',
          errorKey: 'actualPickupDateInputError',
          label: translate('actualPickupDate'),
          inputName: 'bk_dv_actualPickupDate',
        },
      }),
      [translate]
    )

    // Helper to set form fields
    const setFields = React.useCallback(
      fieldToUpdate => {
        setFormValues(prev => ({ ...prev, ...fieldToUpdate }))
      },
      [setFormValues]
    )

    // Event handlers
    const handleDatePickerChange = React.useCallback(
      (type, date) => {
        const fieldConfig = datePickerFieldConfigMap[type]
        // Update form value right away so user can see the date they are selecting
        setFields({ [fieldConfig.formValueKey]: date })
        // Validate date
        const isValid = validateDate({ value: date })
        // Date is invalid, return early and set error message
        if (date === null || date === '' || !isValid) {
          setFields({ [fieldConfig.formValueKey]: null })
          setApplyErrorStyling(prev => ({
            ...prev,
            [fieldConfig.errorKey]: {
              error: true,
              errorMessage: translate('invalidDate'),
            },
          }))
          return
        }

        // Check if the end estimated pickup date is before the start estimated pickup date
        if (fieldConfig.formValueKey === 'endEstimatedPickupDateValue') {
          if (moment(date).isBefore(moment(formValues.startEstimatedPickupDateValue))) {
            setApplyErrorStyling(prev => ({
              ...prev,
              [fieldConfig.errorKey]: {
                error: true,
                errorMessage: translate('bookingEndEstimatedDateError'),
              },
            }))
            return
          }
        }

        // Check if the start estimated pickup date is after the end estimated pickup date
        if (fieldConfig.formValueKey === 'startEstimatedPickupDateValue') {
          if (moment(date).isAfter(moment(formValues.endEstimatedPickupDateValue))) {
            setApplyErrorStyling(prev => ({
              ...prev,
              [fieldConfig.errorKey]: {
                error: true,
                errorMessage: translate('bookingStartEstimatedDateError'),
              },
            }))
            return
          }
        }

        // All checks passed, update the API value and clear any error messages
        setFields({
          [fieldConfig.apiValueKey]: formatDateForApiCall({ date }),
        })
        setApplyErrorStyling(() => ({
          startEstimatedPickupDateInputError: {
            error: false,
            errorMessage: null,
          },
          endEstimatedPickupDateInputError: {
            error: false,
            errorMessage: null,
          },
          actualPickupDateInputError: { error: false, errorMessage: null },
        }))
      },
      [setFields, datePickerFieldConfigMap, formValues, translate]
    )

    const handleBookingNotesChange = React.useCallback(
      (_, { value }) => {
        setFields({ bookingNotes: sanitizedInput(value) })
      },
      [setFields]
    )

    const handleVenueNotesChange = React.useCallback(
      (_, { value }) => {
        setFields({ venueNotes: sanitizedInput(value) })
      },
      [setFields]
    )

    const handleCollectorChange = React.useCallback(
      value => {
        setFields({ collectorDropdownValue: value })
        if (value) {
          setFields({ assignedBy: currentUserEmployee.userId })
          if (
            value === formattedBookingDetails?.preferredCollectorId &&
            formValues.setAsPreferredCollector
          ) {
            setFields({ setAsPreferredCollector: false })
          }
        } else {
          setFields({ assignedBy: null })
          setFields({ setAsPreferredCollector: false })
        }
      },
      [setFields, currentUserEmployee, formattedBookingDetails, formValues]
    )

    const handleStatusChange = React.useCallback(
      (e, { value }) => {
        setFields({ statusDropdownValue: value })
      },
      [setFields]
    )

    const handlePreferredCollectorCheckboxChange = React.useCallback(
      checked => {
        setFields({ setAsPreferredCollector: checked })
      },
      [setFields]
    )

    const handleSaveBtnOnClick = React.useCallback(() => {
      let kegCollectionObjToUpdate = null
      const updatedParams = Object.entries(formValues).reduce((acc, [key, value]) => {
        switch (key) {
          case 'bookingNotes':
            {
              const filtered = formattedBookingDetails.kegCollectionRequestObjects.filter(
                collection =>
                  collection.id === formattedBookingDetails.mostRecentCollectionRequestId
              )
              if (filtered.length && formValues.bookingNotes !== filtered[0].pickupInstruction) {
                kegCollectionObjToUpdate = {
                  ...filtered[0],
                  pickupInstruction: value,
                }
              }
            }
            break
          case 'venueNotes':
            acc.venueNotes = value
            break
          case 'collectorDropdownValue':
            acc.collectorCustomerId = value
            break
          case 'assignedBy':
            acc.assignedBy = value
            break
          case 'statusDropdownValue':
            acc.bookingStatus = value
            break
          case 'startEstimatedPickupDateAPIValue':
            acc.startEstimatedPickupDate = value
            break
          case 'endEstimatedPickupDateAPIValue':
            acc.endEstimatedPickupDate = value
            break
          case 'actualPickupDateAPIValue':
            acc.actualPickupDate = value
            break
          case 'setAsPreferredCollector':
            if (
              formValues.collectorDropdownValue &&
              formValues.collectorDropdownValue !== formattedBookingDetails?.preferredCollectorId &&
              formValues.setAsPreferredCollector
            ) {
              acc.preferredCollectorId = formValues.collectorDropdownValue
            } else {
              acc.preferredCollectorId = null
            }
            break
          default:
            break
        }
        return acc
      }, {})

      const updatedBookingAPIParams = {
        ...formattedBookingDetails.bookingRawData,
        ...updatedParams,
      }

      dispatch(
        createAction(UPDATE_KEG_COLLECTION_BOOKING, {
          bookingId: updatedBookingAPIParams.id,
          updatedBookingParams: updatedBookingAPIParams,
          kegCollectionObjToUpdate,
        })
      )
    }, [formValues, formattedBookingDetails, dispatch])

    const renderedReadOnlyDetails = () => {
      const details = [
        {
          label: translate('bookingReferenceNumber'),
          value: formattedBookingDetails?.bookingReferenceId,
          icon: 'book',
          id: 'bk_dv_bookingReferenceId',
        },
        {
          label: translate('createdBy'),
          value: formattedBookingDetails?.bookingCreatedBy,
          icon: 'user',
          id: 'bk_dv_createdBy',
        },
        {
          label: translate('venueAddress'),
          value: formattedBookingDetails?.bookingRawData?.fullAddress,
          icon: 'marker',
          id: 'bk_dv_venueAddress',
        },
        {
          label: translate('venueOpenTimes'),
          value: formattedBookingDetails?.preferredPickup,
          icon: 'clock',
          id: 'bk_dv_venueOpenTimes',
        },
        {
          label: translate('venueContactPhoneNumber'),
          value: formattedBookingDetails?.phoneNumber || translate('Not available'),
          icon: 'phone',
          id: 'bk_dv_contactPhoneNumber',
        },
        {
          label: translate('Date Requested'),
          value: formatDate(
            formattedBookingDetails?.bookingRawData?.dateCreated,
            dateFormatter,
            true
          ),
          icon: 'calendar',
          id: 'bk_dv_dateRequested',
        },
        {
          label: translate('mostRecentCollectionDate'),
          value:
            formatDate(formattedBookingDetails?.bookingRawData?.lastKegPickupDate, dateFormatter) ||
            translate('Never'),
          icon: 'dolly',
          id: 'bk_dv_mostRecentCollectionDate',
        },
        {
          label: translate('preferredCollector'),
          value:
            getPreferredCollectorName(
              formattedBookingDetails?.preferredCollectorId,
              collectorsList.content || []
            ) || translate('None'),
          icon: 'truck',
          id: 'bk_dv_preferredCollector',
        },
      ]

      return details.map(detail => (
        <div className="bk_dv_readOnlyDetailsSegment" key={detail.id}>
          <label>
            <Icon name={detail.icon} className="bk_dv_iconColor" />{' '}
            <span className="bk_dv_readOnlyDetailsLabel">{detail.label}</span>
          </label>
          <p className="bk_dv_readOnlyDetailsValue">{detail.value}</p>
        </div>
      ))
    }

    const renderedDetailedView = (
      <div className="bk_dv_detailedView animate-into-view-1">
        <BookingsGeneralNotificationMessage />
        {isBookingClosed && (
          <Message
            icon="info circle"
            header={translate('closedBooking')}
            content={translate('closedBookingContent')}
            color="black"
          />
        )}
        <div className="bk_dv_pageHeader">
          <Header
            as="h2"
            color="grey"
            content={`${translate('bookingFor')} ${formattedBookingDetails?.customerName}`}
            subheader={`${translate('bookingDetailedViewSubHeader')}.`}
          />
          <span
            id="bk_dv_bookingStatusBadge"
            className={`bk_dv_statusBadge ${statusBadge.className}`}
            aria-label={`${translate('Status')}: ${translate(statusBadge.status)}`}
          >
            <Icon name={statusBadge.icon} />
            {translate(statusBadge.status)}
          </span>
        </div>

        {/* Read only booking details segment */}
        <div className="bk_dv_parentSegment">
          <Header
            as="h3"
            content={translate('bookingDetails')}
            color="grey"
            subheader={translate('generalInformationAboutTheBooking')}
          />
          <div className="bk_dv_readOnlyDetails">{renderedReadOnlyDetails()}</div>
        </div>

        {/* Booking management details segment - notes, collector assignment, status */}
        <div className="bk_dv_parentSegment">
          <Header
            as="h3"
            color="grey"
            content={translate('bookingManagement')}
            subheader={translate('updateNotesCollectorAssignmentStatusAndPickupDates')}
          />

          <div className="bk_dv_bookingManagementDetailsTopRow">
            <Form>
              <Form.Field>
                <label htmlFor="bk_dv_bookingNotes">{translate('bookingNotes')}</label>
                <BookingDetailsTextarea
                  id="bk_dv_bookingNotes"
                  value={formValues?.bookingNotes}
                  onChange={handleBookingNotesChange}
                  classes="bk_dv_bookingDetailsTextarea"
                  isDisabled={isBookingClosed || bookingData.isEditing}
                />
              </Form.Field>
            </Form>

            <Form>
              <Form.Field>
                <label htmlFor="bk_dv_venueNotes">{translate('venueNotes')}</label>
                <BookingDetailsTextarea
                  id="bk_dv_venueNotes"
                  value={formValues?.venueNotes}
                  onChange={handleVenueNotesChange}
                  classes="bk_dv_bookingDetailsTextarea"
                  isDisabled={isBookingClosed || bookingData.isEditing}
                />
              </Form.Field>
            </Form>

            <Form>
              <Form.Field error={collectorsList.didFetchFail}>
                <CollectorsDropdown
                  dispatch={dispatch}
                  createAction={createAction}
                  collectorsList={collectorsList}
                  collectorCustomerId={formattedBookingDetails?.collectorCustomerId}
                  collectorCustomerName={formattedBookingDetails?.collectorCustomerName}
                  collectorCustomerTapId={formattedBookingDetails?.collectorCustomerTapId}
                  bookingCountryCode={formattedBookingDetails?.addressObject?.country}
                  translate={translate}
                  collectorDropdownValue={formValues?.collectorDropdownValue}
                  onChangeCallback={handleCollectorChange}
                  isDisabled={isBookingClosed || bookingData.isEditing}
                  handlePreferredCollectorCheckboxChange={handlePreferredCollectorCheckboxChange}
                  setAsPreferredCollector={formValues.setAsPreferredCollector}
                  isPreferredCollector={
                    formValues?.collectorDropdownValue ===
                    formattedBookingDetails?.preferredCollectorId
                  }
                />
              </Form.Field>
            </Form>

            <Form>
              <Form.Field>
                <label htmlFor="bk_dv_statusDropdown">{translate('Status')}</label>
                <Dropdown
                  id="bk_dv_statusDropdown"
                  selection
                  fluid
                  value={formValues?.statusDropdownValue?.toUpperCase() || ''}
                  options={statusOptions}
                  onChange={handleStatusChange}
                  disabled={isBookingClosed || bookingData.isEditing}
                />
              </Form.Field>
            </Form>
          </div>

          {/* Booking management details segment - pickup dates */}
          <div className="bk_dv_bookingManagementDetailsBottomRow">
            <Form>
              <Form.Field>
                <label htmlFor={datePickerFieldConfigMap.START_ESTIMATED_PICKUP_DATE.inputName}>
                  {datePickerFieldConfigMap.START_ESTIMATED_PICKUP_DATE.label}
                </label>
                <ReactDatepicker
                  id={datePickerFieldConfigMap.START_ESTIMATED_PICKUP_DATE.inputName}
                  value={formValues?.startEstimatedPickupDateValue}
                  onChange={date => handleDatePickerChange('START_ESTIMATED_PICKUP_DATE', date)}
                  compact
                  showMonthDropdown
                  showYearDropdown
                  onBlur={() => {}}
                  minDate={moment(formattedBookingDetails?.bookingRawData?.dateCreated)}
                  className={
                    applyErrorStyling.startEstimatedPickupDateInputError.error
                      ? 'input-error-semantic-ui'
                      : ''
                  }
                  disabled={isBookingClosed || bookingData.isEditing}
                />
                {applyErrorStyling.startEstimatedPickupDateInputError.error && (
                  <Message
                    negative
                    content={applyErrorStyling.startEstimatedPickupDateInputError.errorMessage}
                  />
                )}
              </Form.Field>
            </Form>

            <Form>
              <Form.Field>
                <label htmlFor={datePickerFieldConfigMap.END_ESTIMATED_PICKUP_DATE.inputName}>
                  {datePickerFieldConfigMap.END_ESTIMATED_PICKUP_DATE.label}
                </label>
                <ReactDatepicker
                  id={datePickerFieldConfigMap.END_ESTIMATED_PICKUP_DATE.inputName}
                  value={formValues?.endEstimatedPickupDateValue}
                  onChange={date => handleDatePickerChange('END_ESTIMATED_PICKUP_DATE', date)}
                  compact
                  showMonthDropdown
                  showYearDropdown
                  onBlur={() => {}}
                  minDate={moment(
                    formattedBookingDetails?.bookingRawData?.startEstimatedPickupDate
                  )}
                  className={
                    applyErrorStyling.endEstimatedPickupDateInputError.error
                      ? 'input-error-semantic-ui'
                      : ''
                  }
                  disabled={isBookingClosed || bookingData.isEditing}
                />
                {applyErrorStyling.endEstimatedPickupDateInputError.error && (
                  <Message
                    negative
                    content={applyErrorStyling.endEstimatedPickupDateInputError.errorMessage}
                  />
                )}
              </Form.Field>
            </Form>
            <Form>
              <Form.Field>
                <label htmlFor={datePickerFieldConfigMap.ACTUAL_PICKUP_DATE.inputName}>
                  {datePickerFieldConfigMap.ACTUAL_PICKUP_DATE.label}
                </label>
                <ReactDatepicker
                  id={datePickerFieldConfigMap.ACTUAL_PICKUP_DATE.inputName}
                  value={formValues?.actualPickupDateValue}
                  onChange={date => handleDatePickerChange('ACTUAL_PICKUP_DATE', date)}
                  compact
                  showMonthDropdown
                  showYearDropdown
                  onBlur={() => {}}
                  minDate={moment(formattedBookingDetails?.bookingRawData?.dateCreated)}
                  className={[
                    applyErrorStyling.actualPickupDateInputError.error
                      ? 'input-error-semantic-ui'
                      : '',
                  ].join(' ')}
                  disabled={isBookingClosed || bookingData.isEditing}
                />
                {applyErrorStyling.actualPickupDateInputError.error && (
                  <Message
                    negative
                    content={applyErrorStyling.actualPickupDateInputError.errorMessage}
                  />
                )}
              </Form.Field>
            </Form>
          </div>

          {/* Booking management details segment - save button */}
          <div className="bk_dv_bookingsManagementSaveBtnRow">
            {isBookingClosed ? (
              <div className="bk_dv_bookingClosedMessage">
                <Icon name="info circle" />
                <p className="bk_dv_bookingClosedMessageText">
                  This booking is closed and cannot be edited.
                </p>
              </div>
            ) : (
              <div
                id="bk_dv_showChangesUnsavedHelperText"
                className={`bk_dv_showChangesUnsavedHelperText${
                  isDirty ? ' bk_dv_showChangesUnsavedHelperText--dirty' : ''
                }`}
              >
                <Icon name={isDirty ? 'warning sign' : 'info circle'} />
                <p>
                  {isDirty
                    ? translate('youHaveUnsavedChanges')
                    : translate('editTheBookingDetailsIfYouWishToMakeAnyUpdates')}
                </p>
              </div>
            )}
            <Button
              id="bk_dv_saveButton"
              color="green"
              type="button"
              className="bk_dv_saveButtonFix"
              disabled={isSaveDisabled}
              onClick={() => handleSaveBtnOnClick()}
              loading={bookingData.isEditing}
            >
              <Icon name="save" />
              {translate('saveChangesBtnLabel')}
            </Button>
          </div>
        </div>

        {/* Keg collection requests segment */}
        <div className="bk_dv_parentSegment">
          <Header
            as="h3"
            color="grey"
            content={translate('kegCollectionRequests')}
            subheader={translate('listOfKegCollectionRequests')}
          />

          <KegCollectionRequestsTable
            formattedBookingDetails={formattedBookingDetails}
            dateFormatter={dateFormatter}
            formatDate={formatDate}
            formatKegCollectionTableRows={formatKegCollectionTableRows}
            dispatch={dispatch}
            createAction={createAction}
            translate={translate}
          />
        </div>
      </div>
    )

    let contentToRender = <></>
    if (bookingData.isFetching) {
      contentToRender = <DetailedViewSkeletonLoader />
    } else if (bookingData.didFetchSucceed) {
      contentToRender = formattedBookingDetails?.bookingId ? (
        renderedDetailedView
      ) : (
        <Message
          icon="info circle"
          header={translate('bookingNotFound')}
          content={translate('bookingNotFoundContent')}
        />
      )
    } else if (bookingData.didFetchFail) {
      contentToRender = (
        <Message
          negative
          icon="exclamation triangle"
          header={translate('Error')}
          content={translate('bookingDetailsLoadError')}
        />
      )
    }

    return contentToRender
  })
)

export default React.memo(KegCollectionBookingDetails)
